import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Sidebar, MobileMenuButton } from "@/components/Sidebar";
import { useAuth } from "@/lib/auth-context";
import { ROLE_LABELS } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LogOut, KeyRound, User as UserIcon } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ToThaiLogo } from "@/components/ToThaiLogo";
import { ShiftPickerBadge } from "@/components/ShiftPicker";

export function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.full_name || user?.email || "?")
    .split(/\s+|@/)
    .filter(Boolean)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  async function handleLogout() {
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen flex bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-[var(--tt-surface)] border-b border-[var(--tt-border)] px-4 lg:px-8 py-3 flex items-center justify-between gap-3 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <MobileMenuButton onClick={() => setMenuOpen(true)} />
            <div className="hidden md:flex items-center gap-3">
              <div className="w-24"><ToThaiLogo layout="inline" size="xs" /></div>
              <div className="hidden lg:block">
                <div className="text-[10px] uppercase tracking-widest text-[var(--tt-muted)] leading-none">Operacje</div>
                <div className="text-sm font-semibold text-[var(--tt-text)] tt-display leading-tight">Control Center</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ShiftPickerBadge />
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-3 rounded-full hover:bg-[var(--tt-surface-2)] px-1.5 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--tt-primary)]"
                  data-testid="user-menu-trigger"
                  aria-label="Menu użytkownika"
                >
                  <div className="text-right hidden sm:block">
                    <div className="text-sm font-medium text-[var(--tt-text)] leading-tight">{user?.full_name}</div>
                    <div className="text-xs text-[var(--tt-muted)]">{ROLE_LABELS[user?.role] || user?.role}</div>
                  </div>
                  <div className="h-9 w-9 rounded-full bg-[var(--tt-primary)] text-[var(--tt-primary-fg)] flex items-center justify-center text-sm font-semibold tt-display">
                    {initials}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="user-menu-content">
                <DropdownMenuLabel className="space-y-0.5">
                  <div className="font-semibold">{user?.full_name || "—"}</div>
                  <div className="text-xs font-normal text-[var(--tt-muted)]">{user?.email}</div>
                  <div className="text-xs font-normal text-[var(--tt-muted)]">
                    Rola: <span className="font-medium text-[var(--tt-text)]">{ROLE_LABELS[user?.role] || user?.role}</span>
                  </div>
                  {user?.initials && (
                    <div className="text-xs font-normal text-[var(--tt-muted)]">
                      Inicjały: <span className="tt-mono font-medium text-[var(--tt-text)]">{user.initials}</span>
                    </div>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/change-password")} data-testid="change-password-menu-item">
                  <KeyRound className="h-4 w-4 mr-2" /> Zmień hasło
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-700 focus:bg-red-50"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Wyloguj
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 max-w-[1400px] w-full mx-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
      {/* Toaster jest globalnie w App.js (line ~90), dostępny na wszystkich routes public+private. */}
    </div>
  );
}
